import { useEffect, useRef, useState } from "react";
import { useIdioma } from "../lib/idioma";

/**
 * Hacer una foto con la cámara, o subir una de la galería.
 *
 * En el mostrador se hace con la tablet y en un ordenador de sobremesa se sube
 * el fichero, así que las dos formas están al mismo nivel: dejar solo la cámara
 * dejaría tirados a los clubes que dan de alta desde el ordenador de la
 * oficina, y dejar solo el fichero obligaría a pasar las fotos del móvil a mano.
 *
 * La imagen no sale de aquí hasta que se hace: se devuelve como fichero y quien
 * lo use decide cuándo subirlo.
 */
export default function CapturaImagen({
  titulo,
  ayuda,
  imagen,
  alCambiar,
  trasera = false,
  obligatoria = false,
  saltada = false,
}: {
  titulo: string;
  ayuda: string;
  imagen: Blob | null;
  alCambiar: (imagen: Blob | null) => void;
  /** La cámara de atrás, que es la que apunta al documento. */
  trasera?: boolean;
  /** Sin ella no se puede dar el alta. Se dice aquí, no al final. */
  obligatoria?: boolean;
  /** Se ha decidido a propósito no hacerla: entonces no falta. */
  saltada?: boolean;
}) {
  const { t } = useIdioma();
  const [camara, setCamara] = useState(false);
  const [error, setError] = useState("");
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const flujo = useRef<MediaStream | null>(null);

  // La vista previa es una URL de objeto: hay que soltarla o se queda la
  // imagen entera en memoria por cada foto que se repita.
  useEffect(() => {
    if (!imagen) {
      setVistaPrevia(null);
      return;
    }
    const url = URL.createObjectURL(imagen);
    setVistaPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [imagen]);

  // La cámara se apaga al cerrar: dejarla encendida deja la luz del portátil
  // dada y a cualquiera le da la sensación de estar siendo grabado.
  useEffect(() => {
    if (!camara) {
      flujo.current?.getTracks().forEach((t) => t.stop());
      flujo.current = null;
      return;
    }

    let vivo = true;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t("ci.sinCamara"));
        setCamara(false);
        return;
      }
      try {
        const m = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: trasera ? "environment" : "user" },
          audio: false,
        });
        if (!vivo) {
          m.getTracks().forEach((t) => t.stop());
          return;
        }
        flujo.current = m;
        if (video.current) {
          video.current.srcObject = m;
          await video.current.play().catch(() => undefined);
        }
      } catch (fallo) {
        const nombre = (fallo as { name?: string })?.name;
        setError(
          nombre === "NotAllowedError"
            ? t("ci.sinPermiso")
            : nombre === "NotFoundError"
              ? t("ci.sinDispositivo")
              : t("ci.falloCamara"),
        );
        setCamara(false);
      }
    })();

    return () => {
      vivo = false;
      flujo.current?.getTracks().forEach((t) => t.stop());
      flujo.current = null;
    };
  }, [camara, trasera]);

  function disparar() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d")?.drawImage(v, 0, 0);
    c.toBlob(
      (b) => {
        if (b) alCambiar(b);
        setCamara(false);
      },
      "image/jpeg",
      0.86,
    );
  }

  return (
    <div className="captura">
      <div className={`captura-marco${obligatoria && !imagen && !saltada ? " falta" : ""}`}>
        {camara ? (
          <video ref={video} playsInline muted />
        ) : vistaPrevia ? (
          <img src={vistaPrevia} alt={titulo} />
        ) : (
          <div className="captura-vacia">
            <span aria-hidden>▢</span>
            <span className="suave pequeno">{ayuda}</span>
          </div>
        )}
      </div>

      <strong className="pequeno">{titulo}</strong>

      {obligatoria && !imagen && !saltada && <span className="falta-foto">{t("ci.obligatoria")}</span>}
      {saltada && !imagen && <span className="suave pequeno">{t("ci.saltada")}</span>}

      {error && <p className="suave pequeno">{error}</p>}

      <div className="fila" style={{ gap: 4, flexWrap: "wrap" }}>
        {camara ? (
          <>
            <button type="button" className="boton" onClick={disparar}>
              {t("ci.hacerFoto")}
            </button>
            <button type="button" className="boton secundario" onClick={() => setCamara(false)}>
              {t("g.cancelar")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="boton secundario"
              onClick={() => {
                setError("");
                setCamara(true);
              }}
            >
              {imagen ? t("ci.repetir") : t("ci.camara")}
            </button>
            <button type="button" className="boton fantasma" onClick={() => entrada.current?.click()}>
              {t("ci.subir")}
            </button>
            {imagen && (
              <button type="button" className="boton fantasma" onClick={() => alCambiar(null)}>
                {t("ci.quitar")}
              </button>
            )}
          </>
        )}
      </div>

      <input
        ref={entrada}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) alCambiar(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
